import { User as UserIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getCountryFlag } from '@/lib/countries'

interface VisitorAvatarProps {
  name: string
  identified?: boolean
  active?: boolean
  live?: boolean // Pulse animation only shows when live (under 5 mins)
  size?: 'sm' | 'md' | 'lg'
  countryCode?: string | null
  className?: string
}

const sizeClasses = {
  sm: 'size-6',
  md: 'size-8',
  lg: 'size-10',
}

const iconSizes = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
}

const textSizes = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
}

const dotSizes = {
  sm: 'size-2.5 -top-0.5 -right-0.5',
  md: 'size-3 -top-0.5 -right-0.5',
  lg: 'size-3.5 top-0 right-0',
}

const flagSizes = {
  sm: 'text-[10px] -bottom-1 -left-0.5',
  md: 'text-[12px] -bottom-1 -left-1',
  lg: 'text-sm -bottom-1 -left-1',
}

export function VisitorAvatar({
  name,
  identified = false,
  active = false,
  live = false,
  size = 'sm',
  countryCode,
  className,
}: VisitorAvatarProps) {
  const flag = getCountryFlag(countryCode)
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center shrink-0 relative',
        sizeClasses[size],
        active ? 'bg-primary/30' : 'bg-primary/20',
        className
      )}
    >
      {identified ? (
        <span className={cn('font-medium text-primary', textSizes[size])}>
          {initials}
        </span>
      ) : (
        <UserIcon className={cn('text-sidebar-secondary-muted', iconSizes[size])} />
      )}

      {/* Live indicator - pulsing dot (only for visitors seen within 5 mins) */}
      {live && (
        <span
          className={cn(
            'absolute rounded-full bg-primary visitor-pulse',
            dotSizes[size]
          )}
        />
      )}

      {/* Country flag overlay */}
      {flag && (
        <span
          className={cn(
            'absolute leading-none drop-shadow-sm',
            flagSizes[size]
          )}
          title={countryCode?.toUpperCase()}
        >
          {flag}
        </span>
      )}
    </div>
  )
}
